import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface PptReportValues {
  discGrade?: string;
  pptValue?: number;
  pressure?: number;
  spurtVolume?: number;
  temperature?: number;
  cakeThickness?: number;
}

@Component({
  selector: 'app-ppt-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ppt-test-report.component.html',
  styleUrl: './ppt-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PptTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected pptReportValues(): PptReportValues | null {
    const discGrade = this.stringValue([
      'discGrade',
      'diskGrade',
      'discSize',
      'discSizeUm',
      'discMicronSize'
    ], this.output) ?? this.stringValue([
      'discGrade',
      'diskGrade',
      'discSize',
      'discSizeUm',
      'discMicronSize'
    ], this.test);
    const temperature = this.firstNumber([
      'temperature',
      'testTemperature',
      'temperatureF',
      'testTemp'
    ], this.output, this.test);
    const pressure = this.firstNumber([
      'pressure',
      'testPressure',
      'pressurePsi',
      'pressureApplied'
    ], this.output, this.test);
    const cakeThickness = this.firstNumber([
      'cakeThickness',
      'pptCake',
      'pptCakeThickness'
    ], this.output, this.test);
    const pptValue = this.firstNumber([
      'pptValue',
      'ppt'
    ], this.output, this.test) ?? this.derivedPptValue();
    const spurtVolume = this.firstNumber([
      'spurtVolume',
      'pptSpurt'
    ], this.output, this.test) ?? this.derivedSpurtVolume();

    if (discGrade === undefined
      && [pptValue, pressure, spurtVolume, temperature, cakeThickness].every(value => value === undefined)) {
      return null;
    }

    return { discGrade, pptValue, pressure, spurtVolume, temperature, cakeThickness };
  }

  protected formatDiscGrade(value: string | undefined): string {
    return value === undefined ? '—' : value;
  }

  protected formatWhole(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(0);
  }

  protected formatDecimal(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(2);
  }

  private derivedPptValue(): number | undefined {
    const volumes = this.pptVolumes();
    return volumes ? (volumes.first + volumes.second) * 2 : undefined;
  }

  private derivedSpurtVolume(): number | undefined {
    const volumes = this.pptVolumes();
    if (!volumes) return undefined;

    const totalVolume = volumes.first + volumes.second;
    const multipliedFirstVolume = volumes.first * 2;
    return multipliedFirstVolume < totalVolume
      ? 0
      : 2 * (multipliedFirstVolume - totalVolume);
  }

  private pptVolumes(): { first: number; second: number } | undefined {
    const first = this.firstNumber([
      'filtrateVolume',
      'firstTubeVolume',
      'tube1Volume'
    ], this.output, this.test);
    const second = this.firstNumber([
      'totalVolume',
      'secondTubeVolume',
      'tube2Volume'
    ], this.output, this.test);

    return first !== undefined && second !== undefined && first >= 0 && second >= 0
      ? { first, second }
      : undefined;
  }

  private firstNumber(
    names: string[],
    ...sources: Array<DesignTestInputDto | DesignTestOutputDto>
  ): number | undefined {
    for (const source of sources) {
      const value = this.numberValue(names, source);
      if (value !== undefined) return value;
    }

    return undefined;
  }

  private numberValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): number | undefined {
    return this.toNumber(this.value(names, source));
  }

  private stringValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): string | undefined {
    return this.value(names, source) ?? undefined;
  }

  private value(names: string[], source: DesignTestInputDto | DesignTestOutputDto): string | null {
    const acceptedNames = new Set(names.map(name => this.normalize(name)));
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const parsedField = fields.find(field => acceptedNames.has(this.normalize(field.label)));
    return parsedField?.value?.trim() || this.xmlValue(source.content, acceptedNames);
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return null;

    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(element.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(element.localName))) {
        continue;
      }

      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }

    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}